import {
  guidanceForCheckId,
  type QualityIssueGuidance,
} from "@enrein/shared-contracts";
import { V10Btn, V10Kicker, V10Pill } from "./V10Primitives.js";

export type StudioQaIssue = {
  readonly checkId: string;
  readonly severity: string;
  readonly message: string;
};

export type ResolvedQaIssueRow = StudioQaIssue & {
  readonly guidance: QualityIssueGuidance;
};

export function shouldShowResolveIssuesPanel(
  qaIssues: readonly StudioQaIssue[] | undefined,
  qaReasons: readonly string[] | undefined,
): boolean {
  return (qaIssues?.length ?? 0) > 0 || (qaReasons?.length ?? 0) > 0;
}

/** Prefer structured qaIssues from persisted run detail; fall back to string qaReasons. */
export function buildResolvedQaIssueRows(
  qaIssues: readonly StudioQaIssue[] | undefined,
  qaReasons: readonly string[] | undefined,
): readonly ResolvedQaIssueRow[] {
  if (qaIssues && qaIssues.length > 0) {
    return qaIssues.map((issue) => ({
      ...issue,
      guidance: guidanceForCheckId(issue.checkId),
    }));
  }
  if (qaReasons && qaReasons.length > 0) {
    return qaReasons.map((message, index) => ({
      checkId: `qa-reason-fallback:${index}`,
      severity: "WARN",
      message,
      guidance: guidanceForCheckId(""),
    }));
  }
  return [];
}

function severityLabel(severity: string): string {
  return severity;
}

function IssueCtaButtons({
  guidance,
  importQueueAvailable,
  workflowBusy,
  onReviewTruthProof,
  onCopyClientIntakeLink,
  onReviewImportConfirmation,
}: {
  readonly guidance: QualityIssueGuidance;
  readonly importQueueAvailable: boolean;
  readonly workflowBusy: boolean;
  readonly onReviewTruthProof: () => void;
  readonly onCopyClientIntakeLink: () => void;
  readonly onReviewImportConfirmation: () => void;
}) {
  const showTruthProof =
    guidance.targetSection === "truth_proof" || guidance.targetSection === "review";
  const showIntake = guidance.targetSection === "intake";
  const showImport =
    guidance.targetSection === "import_confirmation" && importQueueAvailable;
  const showImportFallback =
    guidance.targetSection === "import_confirmation" && !importQueueAvailable;

  if (!showTruthProof && !showIntake && !showImport && !showImportFallback) {
    return null;
  }

  return (
    <div className="v10-hero-actions v10-resolve-issue-actions">
      {showTruthProof ? (
        <V10Btn variant="line" disabled={workflowBusy} onClick={onReviewTruthProof}>
          Review Truth / Proof
        </V10Btn>
      ) : null}
      {showIntake ? (
        <V10Btn variant="gold" disabled={workflowBusy} onClick={onCopyClientIntakeLink}>
          Copy Client Intake Link
        </V10Btn>
      ) : null}
      {showImport ? (
        <V10Btn variant="line" disabled={workflowBusy} onClick={onReviewImportConfirmation}>
          Review Import Confirmation
        </V10Btn>
      ) : null}
      {showImportFallback ? (
        <V10Btn variant="gold" disabled={workflowBusy} onClick={onCopyClientIntakeLink}>
          Copy Client Intake Link
        </V10Btn>
      ) : null}
      {guidance.targetSection === "truth_proof" && !showIntake ? (
        <V10Btn variant="gold" disabled={workflowBusy} onClick={onCopyClientIntakeLink}>
          Copy Client Intake Link
        </V10Btn>
      ) : null}
    </div>
  );
}

export function V10ResolveIssuesPanel({
  qaIssues,
  qaReasons,
  importQueueAvailable,
  workflowBusy,
  onReviewTruthProof,
  onCopyClientIntakeLink,
  onReviewImportConfirmation,
  onRetryBuild,
}: {
  readonly qaIssues: readonly StudioQaIssue[] | undefined;
  readonly qaReasons: readonly string[] | undefined;
  readonly importQueueAvailable: boolean;
  readonly workflowBusy: boolean;
  readonly onReviewTruthProof: () => void;
  readonly onCopyClientIntakeLink: () => void;
  readonly onReviewImportConfirmation: () => void;
  readonly onRetryBuild: () => void;
}) {
  const rows = buildResolvedQaIssueRows(qaIssues, qaReasons);
  if (rows.length === 0) {
    return null;
  }

  const anyNotClientReady = rows.some((row) => !row.guidance.clientReady);

  return (
    <section className="v10-panel-block v10-resolve-issues-panel" aria-label="Resolve quality issues">
      <V10Kicker>Resolve quality issues</V10Kicker>
      <p className="v10-muted">
        Review each issue below, fix the underlying profile or evidence gap, then retry the build when
        ready. Do not share as client-ready until these are resolved.
      </p>
      {anyNotClientReady ? (
        <p className="v10-validation" role="status">
          <V10Pill tone="gold">Not client-ready</V10Pill>
        </p>
      ) : null}
      <ul className="v10-resolve-issues-list">
        {rows.map((row) => (
          <li key={`${row.checkId}:${row.message}`} className="v10-resolve-issue-card">
            <div className="v10-resolve-issue-header">
              <strong>{row.guidance.title}</strong>
              <V10Pill>{severityLabel(row.severity)}</V10Pill>
              {!row.guidance.clientReady ? (
                <V10Pill tone="gold">Not client-ready</V10Pill>
              ) : null}
            </div>
            <p className="v10-muted">
              <strong>Reported:</strong> {row.message}
            </p>
            <p>
              <strong>What this means:</strong> {row.guidance.meaning}
            </p>
            <p>
              <strong>What to fix:</strong> {row.guidance.requiredFix}
            </p>
            <p className="v10-muted">
              <strong>Recommended next step:</strong> {row.guidance.recommendedCta}
            </p>
            <IssueCtaButtons
              guidance={row.guidance}
              importQueueAvailable={importQueueAvailable}
              workflowBusy={workflowBusy}
              onReviewTruthProof={onReviewTruthProof}
              onCopyClientIntakeLink={onCopyClientIntakeLink}
              onReviewImportConfirmation={onReviewImportConfirmation}
            />
          </li>
        ))}
      </ul>
      <div className="v10-hero-actions">
        <V10Btn disabled={workflowBusy} onClick={onRetryBuild}>
          Retry build
        </V10Btn>
      </div>
    </section>
  );
}
